import { request } from '~/services/common';

import { expandLensDataResponse } from './leLensTransport';

type Snapshot = ReturnType<typeof expandLensDataResponse>;

/**
 * Split streamed text into newline-delimited lines in linear time.
 *
 * Only the newly arrived text is searched for a newline, and a line that spans many chunks is
 * joined once when it ends. Re-splitting the whole buffer on every chunk instead costs time in
 * proportion to the square of the line length, which froze the tab for tens of seconds on a
 * card of tens of megabytes.
 */
export function ndjsonLines(onLine: (line: string) => void) {
  let pending: Array<string> = [];
  return {
    push(text: string) {
      const last = text.lastIndexOf('\n');
      if (last === -1) {
        if (text) pending.push(text);
        return;
      }
      pending.push(text.slice(0, last));
      const lines = pending.join('').split('\n');
      pending = last + 1 < text.length ? [text.slice(last + 1)] : [];
      lines.forEach(onLine);
    },
    end() {
      const rest = pending.join('');
      pending = [];
      onLine(rest);
    },
  };
}

export async function loadLensDataStream(
  url: string,
  options: { signal: AbortSignal; headers: Record<string, string>; body: string },
  onSnapshot: (snapshot: Snapshot) => void,
  timeoutMs: number,
  timeoutMessage: string,
): Promise<Snapshot> {
  const controller = new AbortController();
  let timedOut = false;
  const abort = () => controller.abort();
  options.signal.addEventListener('abort', abort, { once: true });
  if (options.signal.aborted) abort();
  const timer = window.setTimeout(() => {
    timedOut = true;
    abort();
  }, timeoutMs);
  let reader: ReadableStreamDefaultReader<Uint8Array> | undefined;
  try {
    const response = (await request(url, {
      ...options,
      method: 'POST',
      signal: controller.signal,
      returnResponse: true,
    })) as Response;
    if (!response.headers.get('content-type')?.includes('application/x-ndjson')) {
      const body = await response.json();
      // A replaced selection may finish reading just as it is cancelled. Do not
      // decode or publish that late JSON population into the caller's scope.
      if (controller.signal.aborted) throw new DOMException('Lens data loading was cancelled.', 'AbortError');
      return expandLensDataResponse(body);
    }
    reader = response.body?.getReader();
    if (!reader) throw new Error('No Lens data stream was returned. Please retry.');
    const decoder = new TextDecoder();
    let partial: Snapshot | undefined;
    let complete: Snapshot | undefined;
    const readLine = (line: string) => {
      if (!line.trim()) return;
      const event = JSON.parse(line);
      if (event.type === 'error') throw new Error(event.message);
      if (event.type === 'complete') {
        complete = expandLensDataResponse(event);
      } else if (event.type === 'card') {
        const snapshot = expandLensDataResponse(event);
        partial = { ...snapshot, cards: [...(partial?.cards ?? []), ...snapshot.cards] };
        if (!controller.signal.aborted) onSnapshot(partial);
      }
    };
    const lines = ndjsonLines(readLine);
    while (!controller.signal.aborted) {
      const { done, value } = await reader.read();
      lines.push(decoder.decode(value, { stream: !done }));
      if (done) {
        lines.end();
        break;
      }
    }
    if (controller.signal.aborted) throw new Error('Lens data loading was cancelled.');
    if (!complete) throw new Error('The Lens data stream ended before all results arrived. Please retry.');
    return complete;
  } catch (error) {
    if (timedOut) throw new Error(timeoutMessage, { cause: error });
    throw error;
  } finally {
    window.clearTimeout(timer);
    options.signal.removeEventListener('abort', abort);
    await reader?.cancel().catch(() => undefined);
  }
}
